import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SingleBillInfoFormManager, SingleBillInfoFormPageStore } from '@ctct/application-layer';
import type { SingleBillInfoFormHooks } from '@ctct/state-client-layer';
import { useAllCountries, useAvailableCurrencies, usePartner, useSingleBill } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';

interface Props {
  initializer: Pick<SingleBillInfoFormManager, 'singleBillInfoFormInit'>;
  pageStore: SingleBillInfoFormPageStore;
  hooks: SingleBillInfoFormHooks;
}

export const useSingleBillInfoFormWidget = ({ initializer, pageStore, hooks }: Props) => {
  const { t } = useTranslation();

  const countriesQuery = useAllCountries();
  const singleBillQuery = useSingleBill();
  const availableCurrencies = useAvailableCurrencies();
  const partnerQuery = usePartner();

  const { lifecycle, ...componentProps } = useMemo(() => {
    const returnValue = initializer.singleBillInfoFormInit({
      pageStore,
      queries: [countriesQuery, singleBillQuery, availableCurrencies, partnerQuery],
      t,
    });

    return {
      ...returnValue,
      hooks,
    };
  }, [hooks, initializer, availableCurrencies, countriesQuery, pageStore, partnerQuery, singleBillQuery, t]);

  useWidgetLifecycle(componentProps.status, lifecycle);

  return componentProps;
};

export type SingleBillInfoFormWidgetProps = ReturnType<typeof useSingleBillInfoFormWidget>;
